import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { 
  ArrowLeft, 
  Plus,
  Users,
  UserPlus,
  Globe,
  Shield,
  AlertTriangle,
  DollarSign,
  Search,
  Trash2,
  Eye,
  CreditCard,
  Rocket,
  FileText,
  Calendar,
  Check,
  CheckCircle,
  Loader2,
  X,
  Building2,
  Upload,
  Image as ImageIcon,
  Phone,
  UserCircle,
  TrendingUp,
  Banknote,
  Clock,
  MapPin,
  PlaneTakeoff,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import OverseasWorkerDetailsModal from '@/components/OverseasWorkerDetailsModal';

// Helper function to format base64 image with proper data URI prefix
const formatBase64Image = (base64String) => {
  if (!base64String) return null;
  if (base64String.startsWith('data:image')) return base64String;
  return `data:image/jpeg;base64,${base64String}`;
};

// Country flags mapping
const COUNTRY_FLAGS = {
  'Egypt': '🇪🇬',
  'Lebanon': '🇱🇧',
  'Qatar': '🇶🇦',
  'Bahrain': '🇧🇭',
  'Saudi Arabia': '🇸🇦',
  'Oman': '🇴🇲',
  'UAE': '🇦🇪',
  'Kuwait': '🇰🇼'
};

// Warranty status info
const WARRANTY_STATUSES = {
  'not_activated': { label: 'Not Activated', color: 'bg-slate-500', textColor: 'text-slate-400', icon: '⚪' },
  'active': { label: 'Active', color: 'bg-orange-500', textColor: 'text-orange-400', icon: '🟠' },
  'warning': { label: 'Expiring Soon', color: 'bg-yellow-500', textColor: 'text-yellow-400', icon: '🟡' },
  'critical': { label: 'Completed', color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: '✅' },
  'completed': { label: 'Completed', color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: '🟢' }
};

const COUNTRIES = ['Egypt', 'Lebanon', 'Qatar', 'Bahrain', 'Saudi Arabia', 'Oman', 'UAE', 'Kuwait'];

const getCurrencySymbol = (currency) => currency === 'USD' ? '$' : 'Ð';

// Format date string (YYYY-MM-DD or ISO) to DD-MM-YYYY
const formatDepartureDate = (dateStr) => {
  if (!dateStr) return '';
  const d = dateStr.split('T')[0];
  const parts = d.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return d;
};

  // Worker Card Component - Premium Glassmorphism Design
  const WorkerCard = ({ worker, clients, onActivate, onView, onPayment, onDelete, onSaveDeparture }) => {
    const flag = COUNTRY_FLAGS[worker.destination_country] || '🌍';
    const statusInfo = WARRANTY_STATUSES[worker.warranty_status] || WARRANTY_STATUSES['not_activated'];
    const client = clients.find(c => c.id === worker.client_id);
    const currency = client?.currency || 'AED';
    const currencySymbol = getCurrencySymbol(currency);
    const remaining = (worker.cost || 0) - (worker.paid || 0);
    const effectiveDeparture = worker.departure_date || worker.warranty_activation_date || '';
    const [editingDeparture, setEditingDeparture] = useState(false);
    const [departureInput, setDepartureInput] = useState('');
    
    // Calculate warranty progress
    let progressPercent = 0;
    if (worker.warranty_activated && worker.warranty_days_remaining !== null) {
      const totalDays = worker.warranty_period * 30;
      const elapsed = totalDays - worker.warranty_days_remaining;
      progressPercent = Math.min(100, Math.max(0, (elapsed / totalDays) * 100));
    }
    
    // Warranty ring color
    const getWarrantyColor = () => {
      if (!worker.warranty_activated) return '#64748B';
      if (worker.warranty_status === 'critical') return '#10B981'; // Emerald/Green for completed
      if (worker.warranty_status === 'warning') return '#F59E0B';
      if (worker.warranty_status === 'completed') return '#10B981';
      return '#10B981';
    };
    
    return (
      <div 
        className="group relative backdrop-blur-xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-white/10 rounded-2xl overflow-hidden shadow-2xl hover:shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)] hover:border-white/20 transition-all duration-500 hover:scale-[1.02]" 
        data-testid={`worker-card-${worker.id}`}
      >
        {/* Glow effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-r from-blue-600/30 via-indigo-600/20 to-purple-600/30 p-5 border-b border-white/10">
          <div className="flex justify-between items-start">
            {/* Worker Photo & Info */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-800 border-2 border-white/20 flex items-center justify-center overflow-hidden shadow-lg">
                  {worker.photo ? (
                    <img src={formatBase64Image(worker.photo)} alt={worker.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl">{flag}</span>
                  )}
                </div>
                {/* Status indicator dot */}
                <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${statusInfo.color} border-2 border-slate-900 flex items-center justify-center`}>
                  <span className="text-xs">{statusInfo.icon}</span>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-lg text-white tracking-tight">{worker.name}</h3>
                <p className="text-sm text-slate-400">{worker.passport_number}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-lg">{flag}</span>
                  <span className="text-xs text-slate-500">{worker.destination_country}</span>
                </div>
              </div>
            </div>
            
            {/* Warranty Ring */}
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
                <circle 
                  cx="32" cy="32" r="28" fill="none" 
                  stroke={getWarrantyColor()} 
                  strokeWidth="4" 
                  strokeLinecap="round"
                  strokeDasharray={`${progressPercent * 1.76} 176`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {worker.warranty_days_remaining ?? '—'}
                </span>
                <span className="text-[8px] text-slate-500 uppercase">days</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Client & Warranty Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Client</p>
              <p className="font-semibold text-white text-sm truncate">{worker.client_name || 'Not assigned'}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Warranty</p>
              <p className="font-semibold text-white text-sm">{worker.warranty_period} months</p>
              {editingDeparture ? (
                <div className="flex items-center gap-1 mt-1.5">
                  <input
                    type="date"
                    value={departureInput}
                    onChange={(e) => setDepartureInput(e.target.value)}
                    className="bg-slate-800 border border-white/20 rounded px-1 py-0.5 text-[10px] text-white w-[110px]"
                    data-testid={`departure-date-input-${worker.id}`}
                  />
                  <button
                    onClick={() => { onSaveDeparture(worker.id, departureInput); setEditingDeparture(false); }}
                    className="text-emerald-400 text-[10px] font-bold px-1 hover:text-emerald-300"
                    data-testid={`departure-date-save-${worker.id}`}
                  >✓</button>
                  <button
                    onClick={() => setEditingDeparture(false)}
                    className="text-rose-400 text-[10px] font-bold px-1 hover:text-rose-300"
                    data-testid={`departure-date-cancel-${worker.id}`}
                  >✕</button>
                </div>
              ) : (
                <p
                  className="flex items-center gap-1 text-[11px] text-sky-300 mt-1.5 cursor-pointer hover:text-sky-200"
                  data-testid={`departure-date-${worker.id}`}
                  title="اضغط لتعديل تاريخ المغادرة"
                  onClick={() => { setDepartureInput((effectiveDeparture || '').split('T')[0]); setEditingDeparture(true); }}
                >
                  <PlaneTakeoff className="h-3 w-3" />
                  المغادرة: {formatDepartureDate(effectiveDeparture) || '—'}
                </p>
              )}
            </div>
          </div>
          
          {/* Financial Summary - Premium Style */}
          <div className="bg-gradient-to-r from-emerald-500/10 via-transparent to-amber-500/10 rounded-xl p-4 border border-white/5">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Cost</p>
                <p className="text-lg font-bold text-amber-400">{currencySymbol}{(worker.cost || 0).toLocaleString()}</p>
              </div>
              <div className="border-x border-white/10">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Paid</p>
                <p className="text-lg font-bold text-emerald-400">{currencySymbol}{(worker.paid || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Due</p>
                <p className={`text-lg font-bold ${remaining > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {currencySymbol}{Math.abs(remaining).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          
          {/* Warranty Progress Bar or Activate Button */}
          {worker.warranty_activated ? (
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Warranty Progress</span>
                <span className={`font-medium ${statusInfo.textColor}`}>{statusInfo.label}</span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ${
                    worker.warranty_status === 'critical' ? 'bg-gradient-to-r from-emerald-500 to-green-600' :
                    worker.warranty_status === 'warning' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                    'bg-gradient-to-r from-emerald-500 to-green-400'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          ) : (
            <Button 
              className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-500/25 border-0" 
              onClick={() => onActivate(worker)}
            >
              <Rocket className="h-4 w-4 mr-2" />
              Activate Warranty
            </Button>
          )}
          
          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              className="flex-1 bg-cyan-500/10 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-500/50"
              onClick={() => onView(worker)}
              data-testid={`view-overseas-worker-${worker.id}`}
            >
              <Eye className="h-4 w-4 mr-2" />
              View
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50"
              onClick={() => onPayment(worker)}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Payment
            </Button>
            <Button 
              variant="outline" 
              className="bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20 hover:border-rose-500/50 px-3"
              onClick={() => onDelete(worker.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };
  

const OverseasWorkersPage = () => {
  const navigate = useNavigate();
  
  // Main data state
  const [workers, setWorkers] = useState([]);
  const [clients, setClients] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalWorkers: 0,
    totalClients: 0,
    activeWarranties: 0,
    criticalWarranties: 0, // Now means "Completed Warranties"
    countryCounts: {},
    financial: { total_cost: 0, total_paid: 0, total_remaining: 0 }
  });
  
  // Modals state
  const [showAddWorkerModal, setShowAddWorkerModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showActivateWarrantyModal, setShowActivateWarrantyModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showClientDetailsModal, setShowClientDetailsModal] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [showWorkerDetailsModal, setShowWorkerDetailsModal] = useState(false);
  
  // Selected items
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Filters
  const [workerSearch, setWorkerSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [warrantyFilter, setWarrantyFilter] = useState('all');
  
  // Form states
  const [workerForm, setWorkerForm] = useState({
    name: '',
    name_arabic: '',
    passport_number: '',
    nationality: '',
    client_id: '',
    destination_country: '',
    warranty_period: '3',
    cost: 0,
    paid: 0,
    photo: '',
    notes: ''
  });
  
  const [clientForm, setClientForm] = useState({
    name: '',
    country: '',
    contact_person: '',
    phone: '',
    email: '',
    currency: 'AED',
    notes: ''
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [transactionForm, setTransactionForm] = useState({
    worker_id: '',
    transaction_type: 'ADD',
    amount: '',
    transaction_date: new Date().toISOString().slice(0, 16),
    notes: ''
  });
  
  const [activationDate, setActivationDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  
  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardData, workersData, clientsData, transactionsData] = await Promise.all([
        apiGet('/api/export/dashboard'),
        apiGet('/api/export/workers'),
        apiGet('/api/export/clients'),
        apiGet('/api/export/transactions')
      ]);
      
      setDashboardStats({
        totalWorkers: dashboardData?.total_workers || 0,
        totalClients: dashboardData?.total_clients || 0,
        activeWarranties: (dashboardData?.warranty_counts?.active || 0) + (dashboardData?.warranty_counts?.warning || 0),
        criticalWarranties: dashboardData?.warranty_counts?.critical || 0,
        countryCounts: dashboardData?.country_counts || {},
        financial: dashboardData?.financial || { total_cost: 0, total_paid: 0, total_remaining: 0 }
      });
      
      setWorkers(workersData?.workers || []);
      setClients(clientsData?.clients || []);
      setTransactions(transactionsData?.transactions || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Get filtered workers
  const filteredWorkers = workers.filter(worker => {
    const matchesSearch = !workerSearch || 
      worker.name?.toLowerCase().includes(workerSearch.toLowerCase()) ||
      worker.passport_number?.toLowerCase().includes(workerSearch.toLowerCase());
    const matchesCountry = countryFilter === 'all' || worker.destination_country === countryFilter;
    const matchesWarranty = warrantyFilter === 'all' || worker.warranty_status === warrantyFilter;
    return matchesSearch && matchesCountry && matchesWarranty;
  });
  
  // Save worker
  const handleSaveWorker = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiPost('/api/export/workers', {
        ...workerForm,
        warranty_period: parseInt(workerForm.warranty_period),
        cost: parseFloat(workerForm.cost) || 0,
        paid: parseFloat(workerForm.paid) || 0
      });
      toast.success('Worker added successfully!');
      setShowAddWorkerModal(false);
      setWorkerForm({
        name: '', name_arabic: '', passport_number: '', nationality: '',
        client_id: '', destination_country: '', warranty_period: '3',
        cost: 0, paid: 0, photo: '', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save worker');
    } finally {
      setSaving(false);
    }
  };
  
  // Save client
  const handleSaveClient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiPost('/api/export/clients', clientForm);
      toast.success('Client added successfully!');
      setShowAddClientModal(false);
      setClientForm({
        name: '', country: '', contact_person: '',
        phone: '', email: '', currency: 'AED', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };
  
  // Activate warranty
  const handleActivateWarranty = async () => {
    if (!selectedWorker) return;
    setSaving(true);
    try {
      await apiPost(`/api/export/workers/${selectedWorker.id}/activate-warranty`, {
        activation_date: activationDate
      });
      toast.success('Warranty activated successfully!');
      setShowActivateWarrantyModal(false);
      setSelectedWorker(null);
      loadData();
    } catch (error) {
      const errorMsg = typeof error === 'string' ? error : (error?.message || 'Failed to activate warranty');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  // Add payment
  const handleAddPayment = async () => {
    if (!selectedWorker || !paymentForm.amount) return;
    setSaving(true);
    try {
      await apiPost(`/api/export/workers/${selectedWorker.id}/payment`, {
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        notes: paymentForm.notes
      });
      toast.success('Payment recorded successfully!');
      setShowAddPaymentModal(false);
      setPaymentForm({ amount: '', payment_date: new Date().toISOString().split('T')[0], notes: '' });
      setSelectedWorker(null);
      loadData();
    } catch (error) {
      const errorMsg = typeof error === 'string' ? error : (error?.message || 'Failed to add payment');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };
  
  // Save transaction
  const handleSaveTransaction = async (e) => {
    e.preventDefault();
    if (!transactionForm.worker_id || !transactionForm.amount) return;
    setSaving(true);
    try {
      const worker = workers.find(w => w.id === transactionForm.worker_id);
      const client = clients.find(c => c.id === worker?.client_id);
      
      await apiPost('/api/export/transactions', {
        worker_id: transactionForm.worker_id,
        worker_name: worker?.name || '',
        client_name: client?.name || '',
        transaction_type: transactionForm.transaction_type,
        amount: parseFloat(transactionForm.amount),
        currency: client?.currency || 'AED',
        notes: transactionForm.notes,
        transaction_date: transactionForm.transaction_date
      });
      toast.success('Transaction saved!');
      setShowAddTransactionModal(false);
      setTransactionForm({
        worker_id: '', transaction_type: 'ADD', amount: '',
        transaction_date: new Date().toISOString().slice(0, 16), notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete worker
  const handleDeleteWorker = async (workerId) => {
    if (!window.confirm('Are you sure you want to delete this worker?')) return;
    try {
      await apiDelete(`/api/export/workers/${workerId}`);
      toast.success('Worker deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete worker');
    }
  };
  
  // Delete client
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await apiDelete(`/api/export/clients/${clientId}`);
      toast.success('Client deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };
  
  // Delete transaction
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await apiDelete(`/api/export/transactions/${transactionId}`);
      toast.success('Transaction deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };
  
  // View client details
  const viewClientDetails = (client) => {
    setSelectedClient(client);
    setShowClientDetailsModal(true);
  };
  
  // Get client workers
  const getClientWorkers = (clientId) => {
    return workers.filter(w => w.client_id === clientId);
  };
  
  // Calculate client financials
  const getClientFinancials = (clientId) => {
    const clientWorkers = getClientWorkers(clientId);
    const totalCost = clientWorkers.reduce((sum, w) => sum + (w.cost || 0), 0);
    const totalPaid = clientWorkers.reduce((sum, w) => sum + (w.paid || 0), 0);
    return { totalCost, totalPaid, remaining: totalCost - totalPaid };
  };
  
  // Save departure date for a worker
  const handleSaveDepartureDate = async (workerId, dateValue) => {
    try {
      await apiPut(`/api/export/workers/${workerId}`, { departure_date: dateValue });
      toast.success('تم حفظ تاريخ المغادرة');
      loadData();
    } catch (error) {
      toast.error('فشل حفظ تاريخ المغادرة');
    }
  };
  
  // Render warranty badge
  const WarrantyBadge = ({ status }) => {
    const info = WARRANTY_STATUSES[status] || WARRANTY_STATUSES['not_activated'];
    return (
      <Badge className={`${info.color} text-white`}>
        {info.icon} {info.label}
      </Badge>
    );
  };
  
  // WorkerCard moved to module scope (stable identity preserves inline edit state)

  // Client Card Component - Premium Design
  const ClientCard = ({ client }) => {
    const flag = COUNTRY_FLAGS[client.country] || '🌍';
    const currency = client.currency || 'AED';
    const currencySymbol = getCurrencySymbol(currency);
    const financials = getClientFinancials(client.id);
    const workerCount = getClientWorkers(client.id).length;
    
    return (
      <div 
        className="group relative backdrop-blur-xl bg-gradient-to-br from-amber-500/10 via-slate-800/60 to-slate-900/80 border border-amber-500/20 rounded-2xl p-5 cursor-pointer hover:border-amber-500/40 hover:shadow-[0_0_30px_-5px_rgba(245,158,11,0.2)] transition-all duration-500 hover:scale-[1.02]"
        onClick={() => viewClientDetails(client)}
        data-testid={`client-card-${client.id}`}
      >
        {/* Top section */}
        <div className="flex justify-between items-start mb-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center text-3xl shadow-lg">
              {flag}
            </div>
            <div>
              <h3 className="font-bold text-lg text-white">{client.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-slate-400">{client.country}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  currency === 'AED' 
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                    : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                }`}>
                  {currencySymbol} {currency}
                </span>
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-rose-400 hover:bg-rose-500/20 hover:text-rose-300 rounded-xl"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClient(client.id);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/5 border border-white/5 p-4 rounded-xl text-center">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Workers</p>
            <p className="text-2xl font-bold text-emerald-400">{workerCount}</p>
          </div>
          <div className="bg-white/5 border border-white/5 p-4 rounded-xl text-center">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Outstanding</p>
            <p className={`text-2xl font-bold ${financials.remaining > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
              {currencySymbol}{financials.remaining.toLocaleString()}
            </p>
          </div>
        </div>
        
        {/* Contact */}
        {client.contact_person && (
          <div className="flex items-center gap-2 text-sm text-slate-400 bg-white/5 rounded-xl px-3 py-2">
            <UserCircle className="h-4 w-4" />
            <span>{client.contact_person}</span>
          </div>
        )}
        {client.phone && (
          <p className="text-sm text-slate-500 mt-2 flex items-center gap-2">
            <Phone className="h-3 w-3" /> {client.phone}
          </p>
        )}
        
        <p className="text-center text-xs text-amber-400/70 font-medium mt-4 group-hover:text-amber-400 transition-colors">
          Click to view details →
        </p>
      </div>
    );
  };
  
  return (
    <div className="space-y-6 pb-20" data-testid="overseas-workers-page">
      {/* Header - Premium Design */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="h-12 w-12 rounded-2xl bg-white/5 hover:bg-white/10 text-white border border-white/10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <Globe className="h-8 w-8 text-amber-400" />
              Overseas Workers
              <span className="text-amber-400">/ العمالة الخارجية</span>
            </h1>
            <p className="text-sm text-slate-400 mt-1">Premium Workforce Management System</p>
          </div>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setShowAddWorkerModal(true)}
          data-testid="add-worker-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Worker
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-5">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg">
            📊 Dashboard
          </TabsTrigger>
          <TabsTrigger value="workers" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg">
            👷 Workers
          </TabsTrigger>
          <TabsTrigger value="clients" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg">
            👥 Clients
          </TabsTrigger>
          <TabsTrigger value="warranties" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg">
            ⭐ Warranties
          </TabsTrigger>
          <TabsTrigger value="reports" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg">
            📈 Reports
          </TabsTrigger>
        </TabsList>
        
        {/* Dashboard Tab - Premium Design */}
        <TabsContent value="dashboard" className="mt-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
            </div>
          ) : (
            <>
              {/* Premium Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div 
                  className="group backdrop-blur-xl bg-gradient-to-br from-emerald-500/20 via-slate-800/60 to-slate-900/80 border border-emerald-500/30 rounded-2xl p-5 hover:border-emerald-500/50 hover:shadow-[0_0_30px_-5px_rgba(16,185,129,0.3)] transition-all duration-500 cursor-pointer"
                  onClick={() => setActiveTab('workers')}
                  data-testid="stats-total-workers"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Users className="h-6 w-6 text-emerald-400" />
                    </div>
                    <TrendingUp className="h-5 w-5 text-emerald-400/50 group-hover:scale-110 transition-transform" />
                  </div>
                  <p className="text-4xl font-bold text-white mb-1">{dashboardStats.totalWorkers}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Total Workers / العمال</p>
                </div>
                
                <div 
                  className="group backdrop-blur-xl bg-gradient-to-br from-amber-500/20 via-slate-800/60 to-slate-900/80 border border-amber-500/30 rounded-2xl p-5 hover:border-amber-500/50 hover:shadow-[0_0_30px_-5px_rgba(245,158,11,0.3)] transition-all duration-500 cursor-pointer"
                  onClick={() => setActiveTab('clients')}
                  data-testid="stats-total-clients"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-amber-400" />
                    </div>
                    <TrendingUp className="h-5 w-5 text-amber-400/50 group-hover:scale-110 transition-transform" />
                  </div>
                  <p className="text-4xl font-bold text-white mb-1">{dashboardStats.totalClients}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Clients / العملاء</p>
                </div>
                
                <div 
                  className="group backdrop-blur-xl bg-gradient-to-br from-blue-500/20 via-slate-800/60 to-slate-900/80 border border-blue-500/30 rounded-2xl p-5 hover:border-blue-500/50 hover:shadow-[0_0_30px_-5px_rgba(59,130,246,0.3)] transition-all duration-500 cursor-pointer"
                  onClick={() => setActiveTab('warranties')}
                  data-testid="stats-active-warranties"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Shield className="h-6 w-6 text-blue-400" />
                    </div>
                    <Clock className="h-5 w-5 text-blue-400/50 group-hover:scale-110 transition-transform" />
                  </div>
                  <p className="text-4xl font-bold text-white mb-1">{dashboardStats.activeWarranties}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Active Warranties / نشط</p>
                </div>
                
                <div 
                  className="group backdrop-blur-xl bg-gradient-to-br from-emerald-500/20 via-slate-800/60 to-slate-900/80 border border-emerald-500/30 rounded-2xl p-5 hover:border-emerald-500/50 hover:shadow-[0_0_30px_-5px_rgba(16,185,129,0.3)] transition-all duration-500 cursor-pointer"
                  onClick={() => setActiveTab('warranties')}
                  data-testid="stats-completed-warranties"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-emerald-400" />
                    </div>
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                    </span>
                  </div>
                  <p className="text-4xl font-bold text-white mb-1">{dashboardStats.criticalWarranties}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Completed / تم الضمان</p>
                </div>
              </div>

              {/* Warranty Alerts Section - NEW */}
              {(() => {
                const expiringWarranties = workers.filter(w => 
                  w.warranty_activated && 
                  w.warranty_days_remaining !== null && 
                  w.warranty_days_remaining > 0 && 
                  w.warranty_days_remaining <= 90
                ).sort((a, b) => a.warranty_days_remaining - b.warranty_days_remaining);
                
                if (expiringWarranties.length > 0) {
                  return (
                    <div className="backdrop-blur-xl bg-gradient-to-br from-amber-500/10 via-slate-800/60 to-slate-900/80 border border-amber-500/30 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                            <AlertTriangle className="h-5 w-5 text-amber-400" />
                          </div>
                          <div>
                            <h3 className="font-bold text-white text-lg">Warranty Alerts</h3>
                            <p className="text-xs text-slate-500">تنبيهات الضمان - انتهاء قريب</p>
                          </div>
                        </div>
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                          {expiringWarranties.length} تنبيه
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {expiringWarranties.slice(0, 6).map(worker => {
                          const flag = COUNTRY_FLAGS[worker.destination_country] || '🌍';
                          const isUrgent = worker.warranty_days_remaining <= 30;
                          const isWarning = worker.warranty_days_remaining <= 60 && worker.warranty_days_remaining > 30;
                          
                          return (
                            <div 
                              key={worker.id}
                              className={`p-4 rounded-xl border transition-all cursor-pointer hover:scale-[1.02] ${
                                isUrgent 
                                  ? 'bg-rose-500/10 border-rose-500/30 hover:border-rose-500/50' 
                                  : isWarning
                                  ? 'bg-amber-500/10 border-amber-500/30 hover:border-amber-500/50'
                                  : 'bg-blue-500/10 border-blue-500/30 hover:border-blue-500/50'
                              }`}
                              onClick={() => {
                                setSelectedWorker(worker);
                                setShowWorkerDetailsModal(true);
                              }}
                              data-testid={`warranty-alert-${worker.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden">
                                  {worker.photo ? (
                                    <img src={formatBase64Image(worker.photo)} alt={worker.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-xl">{flag}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-white text-sm truncate">{worker.name}</p>
                                  <p className="text-xs text-slate-500 truncate">{worker.client_name}</p>
                                </div>
                                <div className="text-right">
                                  <p className={`text-lg font-bold ${
                                    isUrgent ? 'text-rose-400' : isWarning ? 'text-amber-400' : 'text-blue-400'
                                  }`}>
                                    {worker.warranty_days_remaining}
                                  </p>
                                  <p className="text-[10px] text-slate-500">يوم متبقي</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Two Column Layout: Country Distribution + Financial Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Country Distribution - Premium Map Style */}
                <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">Workers by Country</h3>
                      <p className="text-xs text-slate-500">توزيع العمال حسب الدولة</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {COUNTRIES.map(country => {
                      const count = dashboardStats.countryCounts[country] || 0;
                      const hasWorkers = count > 0;
                      const totalWorkers = dashboardStats.totalWorkers || 1;
                      const percentage = Math.round((count / totalWorkers) * 100);
                      
                      return (
                        <div 
                          key={country} 
                          className={`text-center p-3 rounded-xl border transition-all duration-300 cursor-pointer hover:scale-105 ${
                            hasWorkers 
                              ? 'bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/50' 
                              : 'bg-white/5 border-white/10 hover:border-white/20'
                          }`}
                          onClick={() => {
                            setCountryFilter(country);
                            setActiveTab('workers');
                          }}
                          data-testid={`country-filter-${country}`}
                        >
                          <span className="text-2xl block mb-1">{COUNTRY_FLAGS[country]}</span>
                          <p className={`text-xl font-bold ${hasWorkers ? 'text-emerald-400' : 'text-slate-600'}`}>
                            {count}
                          </p>
                          <p className="text-[9px] text-slate-500 font-medium truncate">{country}</p>
                          {hasWorkers && (
                            <div className="mt-2 h-1 bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-emerald-500 to-green-400 rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Financial Overview with Chart */}
                <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">Financial Overview</h3>
                      <p className="text-xs text-slate-500">نظرة عامة على الماليات</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Donut Chart */}
                    <div className="relative w-32 h-32 flex-shrink-0">
                      <svg className="w-32 h-32 -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="12" />
                        {(() => {
                          const total = dashboardStats.financial.total_cost || 1;
                          const paid = dashboardStats.financial.total_paid || 0;
                          const paidPercent = Math.round((paid / total) * 100);
                          const circumference = 2 * Math.PI * 40;
                          const paidOffset = circumference - (paidPercent / 100) * circumference;
                          
                          return (
                            <circle 
                              cx="50" cy="50" r="40" 
                              fill="none" 
                              stroke="url(#gradient-paid)" 
                              strokeWidth="12" 
                              strokeLinecap="round"
                              strokeDasharray={circumference}
                              strokeDashoffset={paidOffset}
                              className="transition-all duration-1000"
                            />
                          );
                        })()}
                        <defs>
                          <linearGradient id="gradient-paid" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#10B981" />
                            <stop offset="100%" stopColor="#34D399" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl font-bold text-white">
                          {Math.round((dashboardStats.financial.total_paid / (dashboardStats.financial.total_cost || 1)) * 100)}%
                        </span>
                        <span className="text-[10px] text-slate-500">مدفوع</span>
                      </div>
                    </div>
                    
                    {/* Financial Details */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Banknote className="h-4 w-4 text-cyan-400" />
                          <span className="text-xs text-slate-400">التكلفة</span>
                        </div>
                        <span className="font-bold text-cyan-400">{dashboardStats.financial.total_cost?.toLocaleString() || 0} AED</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-emerald-400" />
                          <span className="text-xs text-slate-400">المدفوع</span>
                        </div>
                        <span className="font-bold text-emerald-400">{dashboardStats.financial.total_paid?.toLocaleString() || 0} AED</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-rose-400" />
                          <span className="text-xs text-slate-400">المتبقي</span>
                        </div>
                        <span className="font-bold text-rose-400">{dashboardStats.financial.total_remaining?.toLocaleString() || 0} AED</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Two Column: Transactions + Recent Workers */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Financial Transactions - Premium Table */}
                <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-white/10 rounded-2xl p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                        <CreditCard className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">Transactions</h3>
                        <p className="text-xs text-slate-500">المعاملات المالية</p>
                      </div>
                    </div>
                    <Button 
                      className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white border-0 shadow-lg shadow-purple-500/25"
                      onClick={() => setShowAddTransactionModal(true)}
                      data-testid="add-transaction-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  </div>
                  
                  {transactions.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <CreditCard className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                      <p>No transactions yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                      {transactions.slice(0, 10).map(t => {
                        const typeStyles = {
                          'ADD': { bg: 'bg-emerald-500/20', color: 'text-emerald-400', border: 'border-emerald-500/30' },
                          'DEDUCT': { bg: 'bg-rose-500/20', color: 'text-rose-400', border: 'border-rose-500/30' },
                          'ADJUST': { bg: 'bg-blue-500/20', color: 'text-blue-400', border: 'border-blue-500/30' }
                        };
                        const style = typeStyles[t.transaction_type] || typeStyles['ADD'];
                        const currencySymbol = t.currency === 'USD' ? '$' : 'Ð';
                        const date = new Date(t.created_at);
                        
                        return (
                          <div 
                            key={t.id} 
                            className={`p-4 rounded-xl border ${style.border} ${style.bg} hover:border-white/30 transition-all`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-white text-sm truncate">{t.worker_name}</p>
                                <p className="text-xs text-slate-500 truncate">{t.client_name || 'N/A'}</p>
                              </div>
                              <div className="text-right flex items-center gap-3">
                                <div>
                                  <p className={`font-bold ${style.color}`}>
                                    {t.transaction_type === 'DEDUCT' ? '-' : '+'}{currencySymbol}{Math.abs(t.amount).toLocaleString()}
                                  </p>
                                  <p className="text-[10px] text-slate-500">{date.toLocaleDateString()}</p>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-rose-400 hover:bg-rose-500/20 h-8 w-8 p-0"
                                  onClick={() => handleDeleteTransaction(t.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Recent Workers - Premium Style */}
                <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-white/10 rounded-2xl p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <Users className="h-5 w-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">Recent Workers</h3>
                        <p className="text-xs text-slate-500">آخر العمال المضافين</p>
                      </div>
                    </div>
                  <Button 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10"
                    onClick={() => setActiveTab('workers')}
                  >
                    View All →
                  </Button>
                </div>
                
                {workers.length === 0 ? (
                  <div className="text-center py-10 text-slate-400">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                    <p>No workers yet</p>
                    <Button className="mt-4 bg-blue-600" onClick={() => setShowAddWorkerModal(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Worker
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {workers.slice(0, 8).map(worker => {
                      const flag = COUNTRY_FLAGS[worker.destination_country] || '🌍';
                      const statusInfo = WARRANTY_STATUSES[worker.warranty_status] || WARRANTY_STATUSES['not_activated'];
                      const remaining = (worker.cost || 0) - (worker.paid || 0);
                      
                      return (
                        <div 
                          key={worker.id} 
                          className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all cursor-pointer group"
                          onClick={() => {
                            setSelectedWorker(worker);
                            setShowWorkerDetailsModal(true);
                          }}
                          data-testid={`recent-worker-${worker.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-xl overflow-hidden">
                              {worker.photo ? (
                                <img src={formatBase64Image(worker.photo)} alt={worker.name} className="w-full h-full object-cover" />
                              ) : (
                                <span>{flag}</span>
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-white group-hover:text-emerald-400 transition-colors">{worker.name}</p>
                              <p className="text-xs text-slate-500">{worker.client_name || 'N/A'}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-slate-500">{worker.destination_country}</span>
                                {remaining > 0 && (
                                  <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30 text-[10px] py-0">
                                    Due: {remaining.toLocaleString()}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusInfo.color} text-white`}>
                                {statusInfo.label}
                              </span>
                              {worker.warranty_days_remaining !== null && (
                                <p className="text-[10px] text-slate-500 mt-1">
                                  {worker.warranty_days_remaining > 0 ? `${worker.warranty_days_remaining} days` : 'Expired'}
                                </p>
                              )}
                            </div>
                            <Eye className="h-4 w-4 text-slate-500 group-hover:text-white transition-colors" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            </>
          )}
        </TabsContent>
        
        {/* Workers Tab - Premium Style */}
        <TabsContent value="workers" className="mt-6 space-y-4">
          {/* Filters */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/60 to-slate-900/80 border border-white/10 rounded-2xl p-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="🔍 Search by name or passport..."
                  value={workerSearch}
                  onChange={(e) => setWorkerSearch(e.target.value)}
                  className="bg-white"
                />
              </div>
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {COUNTRIES.map(country => (
                    <SelectItem key={country} value={country}>{COUNTRY_FLAGS[country]} {country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={warrantyFilter} onValueChange={setWarrantyFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Warranties" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  <SelectItem value="all" className="text-white">All Warranties</SelectItem>
                  {Object.entries(WARRANTY_STATUSES).map(([key, info]) => (
                    <SelectItem key={key} value={key} className="text-white">{info.icon} {info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Workers Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
            </div>
          ) : filteredWorkers.length === 0 ? (
            <div className="text-center py-20">
              <Users className="h-16 w-16 mx-auto mb-4 text-slate-600" />
              <p className="text-lg text-slate-400 mb-2">No workers found</p>
              <Button 
                className="mt-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white border-0 shadow-lg shadow-emerald-500/25" 
                onClick={() => setShowAddWorkerModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Worker
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredWorkers.map(worker => (
                <WorkerCard
                  key={worker.id}
                  worker={worker}
                  clients={clients}
                  onActivate={(w) => { setSelectedWorker(w); setShowActivateWarrantyModal(true); }}
                  onView={(w) => { setSelectedWorker(w); setShowWorkerDetailsModal(true); }}
                  onPayment={(w) => { setSelectedWorker(w); setShowAddPaymentModal(true); }}
                  onDelete={handleDeleteWorker}
                  onSaveDeparture={handleSaveDepartureDate}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Clients Tab - Premium Style */}
        <TabsContent value="clients" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-800 text-lg">👥 Clients List</h3>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowAddClientModal(true)}
              data-testid="add-client-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Client
            </Button>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <span className="text-6xl block mb-4">👥</span>
              <p className="text-lg mb-2">No clients yet</p>
              <Button className="mt-4 bg-blue-600" onClick={() => setShowAddClientModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map(client => (
                <ClientCard key={client.id} client={client} />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Warranties Tab */}
        <TabsContent value="warranties" className="mt-6 space-y-4">
          <Card className="bg-white p-6">
            <h3 className="font-bold text-slate-800 mb-4">⭐ Warranty Tracking</h3>
            
            {/* Warranty Stats */}
            <div className="grid grid-cols-5 gap-3 mb-6">
              {Object.entries(WARRANTY_STATUSES).map(([key, info]) => {
                const count = workers.filter(w => w.warranty_status === key).length;
                return (
                  <div key={key} className={`p-3 rounded-lg text-center border ${info.color}/20 border-${info.color.replace('bg-', '')}/30`}>
                    <p className={`text-2xl font-bold ${info.textColor}`}>{count}</p>
                    <p className="text-xs text-slate-500 font-semibold">{info.label}</p>
                  </div>
                );
              })}
            </div>
            
            {/* Warranties List */}
            <div className="space-y-3">
              {workers.filter(w => w.warranty_activated).length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <span className="text-4xl block mb-2">⏳</span>
                  <p>No activated warranties</p>
                </div>
              ) : (
                workers.filter(w => w.warranty_activated).map(worker => {
                  const flag = COUNTRY_FLAGS[worker.destination_country] || '🌍';
                  const statusInfo = WARRANTY_STATUSES[worker.warranty_status] || WARRANTY_STATUSES['active'];
                  
                  return (
                    <div key={worker.id} className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl">
                      <div className="flex items-center gap-4">
                        <span className="text-3xl">{flag}</span>
                        <div>
                          <p className="font-semibold text-slate-800">{worker.name}</p>
                          <p className="text-sm text-slate-500">{worker.client_name} • {worker.warranty_period} months</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <WarrantyBadge status={worker.warranty_status} />
                        <p className="text-sm text-slate-500 mt-1">
                          {worker.warranty_days_remaining !== null 
                            ? (worker.warranty_days_remaining > 0 ? `${worker.warranty_days_remaining} days left` : 'Expired')
                            : ''
                          }
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </TabsContent>
        
        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-6">
          <Card className="bg-white p-6">
            <h3 className="font-bold text-slate-800 mb-4">📈 Client Reports</h3>
            <p className="text-slate-500">Select a client to generate detailed reports including workers, payments, and warranty status.</p>
            
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map(client => {
                const flag = COUNTRY_FLAGS[client.country] || '🌍';
                const financials = getClientFinancials(client.id);
                const workerCount = getClientWorkers(client.id).length;
                
                return (
                  <Card 
                    key={client.id} 
                    className="p-4 cursor-pointer hover:shadow-md transition-all border border-slate-200 hover:border-blue-300"
                    onClick={() => viewClientDetails(client)}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{flag}</span>
                      <div>
                        <p className="font-semibold text-slate-800">{client.name}</p>
                        <p className="text-sm text-slate-500">{client.country}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-sm">
                      <div className="bg-slate-100 p-2 rounded">
                        <p className="font-bold text-blue-600">{workerCount}</p>
                        <p className="text-xs text-slate-500">Workers</p>
                      </div>
                      <div className="bg-slate-100 p-2 rounded">
                        <p className="font-bold text-emerald-600">{getCurrencySymbol(client.currency)}{financials.totalPaid.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Paid</p>
                      </div>
                      <div className="bg-slate-100 p-2 rounded">
                        <p className={`font-bold ${financials.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {getCurrencySymbol(client.currency)}{financials.remaining.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">Due</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Add Worker Modal */}
      <Dialog open={showAddWorkerModal} onOpenChange={setShowAddWorkerModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>➕ Add New Worker</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveWorker}>
            <div className="space-y-6">
              {/* Photo Upload - NEW */}
              <div>
                <h4 className="text-purple-600 font-semibold mb-3">📷 Worker Photo</h4>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
                    {workerForm.photo ? (
                      <img src={formatBase64Image(workerForm.photo)} alt="Worker" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="h-8 w-8 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="worker-photo" className="cursor-pointer">
                      <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors w-fit">
                        <Upload className="h-4 w-4" />
                        <span className="font-medium">Upload Photo</span>
                      </div>
                    </Label>
                    <Input 
                      id="worker-photo"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setWorkerForm({...workerForm, photo: reader.result});
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <p className="text-xs text-slate-500 mt-2">JPG, PNG or GIF. Max 2MB</p>
                    {workerForm.photo && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 mt-1"
                        onClick={() => setWorkerForm({...workerForm, photo: ''})}
                      >
                        <X className="h-3 w-3 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Basic Info */}
              <div>
                <h4 className="text-emerald-600 font-semibold mb-3">📋 Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name (English) *</Label>
                    <Input 
                      value={workerForm.name}
                      onChange={(e) => setWorkerForm({...workerForm, name: e.target.value})}
                      required
                      placeholder="WORKER NAME"
                    />
                  </div>
                  <div>
                    <Label>Name (Arabic)</Label>
                    <Input 
                      value={workerForm.name_arabic}
                      onChange={(e) => setWorkerForm({...workerForm, name_arabic: e.target.value})}
                      placeholder="اسم العاملة"
                    />
                  </div>
                  <div>
                    <Label>Passport Number *</Label>
                    <Input 
                      value={workerForm.passport_number}
                      onChange={(e) => setWorkerForm({...workerForm, passport_number: e.target.value})}
                      required
                      placeholder="A12345678"
                    />
                  </div>
                  <div>
                    <Label>Nationality *</Label>
                    <Input 
                      value={workerForm.nationality}
                      onChange={(e) => setWorkerForm({...workerForm, nationality: e.target.value})}
                      required
                      placeholder="INDONESIA"
                    />
                  </div>
                </div>
              </div>
              
              {/* Destination Info */}
              <div>
                <h4 className="text-amber-600 font-semibold mb-3">🌍 Destination Info</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Client *</Label>
                    <Select 
                      value={workerForm.client_id} 
                      onValueChange={(v) => setWorkerForm({...workerForm, client_id: v})}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Client..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {COUNTRY_FLAGS[client.country]} {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Country *</Label>
                    <Select 
                      value={workerForm.destination_country} 
                      onValueChange={(v) => setWorkerForm({...workerForm, destination_country: v})}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Country..." />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(country => (
                          <SelectItem key={country} value={country}>
                            {COUNTRY_FLAGS[country]} {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* Warranty Info */}
              <div>
                <h4 className="text-emerald-600 font-semibold mb-3">⭐ Warranty Period</h4>
                <div>
                  <Label>Warranty Period *</Label>
                  <Select 
                    value={workerForm.warranty_period} 
                    onValueChange={(v) => setWorkerForm({...workerForm, warranty_period: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Financial Info */}
              <div>
                <h4 className="text-blue-600 font-semibold mb-3">💰 Contract Value</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Cost *</Label>
                    <Input 
                      type="number"
                      value={workerForm.cost}
                      onChange={(e) => setWorkerForm({...workerForm, cost: e.target.value})}
                      min="0"
                      required
                    />
                  </div>
                  <div>
                    <Label>Amount Paid</Label>
                    <Input 
                      type="number"
                      value={workerForm.paid}
                      onChange={(e) => setWorkerForm({...workerForm, paid: e.target.value})}
                      min="0"
                    />
                  </div>
                </div>
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Remaining:</span>
                    <span className="font-bold text-orange-600">
                      {((parseFloat(workerForm.cost) || 0) - (parseFloat(workerForm.paid) || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={workerForm.notes}
                  onChange={(e) => setWorkerForm({...workerForm, notes: e.target.value})}
                  placeholder="Any additional notes..."
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddWorkerModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                💾 Save Worker
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Add Client Modal */}
      <Dialog open={showAddClientModal} onOpenChange={setShowAddClientModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>➕ Add New Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveClient}>
            <div className="space-y-4">
              <div>
                <Label>Client Name *</Label>
                <Input 
                  value={clientForm.name}
                  onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                  required
                  placeholder="Company or Person Name"
                />
              </div>
              <div>
                <Label>Country *</Label>
                <Select 
                  value={clientForm.country} 
                  onValueChange={(v) => setClientForm({...clientForm, country: v})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Country..." />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>
                        {COUNTRY_FLAGS[country]} {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input 
                  value={clientForm.contact_person}
                  onChange={(e) => setClientForm({...clientForm, contact_person: e.target.value})}
                  placeholder="Contact Name"
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input 
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                  placeholder="+966..."
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label>Currency *</Label>
                <Select 
                  value={clientForm.currency} 
                  onValueChange={(v) => setClientForm({...clientForm, currency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AED">AED (Ð) - UAE Dirham</SelectItem>
                    <SelectItem value="USD">USD ($) - US Dollar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={clientForm.notes}
                  onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                  placeholder="Any notes..."
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddClientModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                💾 Save Client
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Activate Warranty Modal */}
      <Dialog open={showActivateWarrantyModal} onOpenChange={setShowActivateWarrantyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🚀 Activate Warranty</DialogTitle>
            <DialogDescription>
              The warranty will be activated from the selected date.
            </DialogDescription>
          </DialogHeader>
          
          {selectedWorker && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{COUNTRY_FLAGS[selectedWorker.destination_country] || '🌍'}</span>
                <div>
                  <p className="font-semibold text-slate-800">{selectedWorker.name}</p>
                  <p className="text-sm text-slate-500">{selectedWorker.destination_country} • {selectedWorker.warranty_period} months warranty</p>
                </div>
              </div>
            </div>
          )}
          
          <div>
            <Label>Activation Date</Label>
            <Input 
              type="date"
              value={activationDate}
              onChange={(e) => setActivationDate(e.target.value)}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivateWarrantyModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleActivateWarranty} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              🚀 Activate Warranty
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Payment Modal */}
      <Dialog open={showAddPaymentModal} onOpenChange={setShowAddPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>💰 Add Payment</DialogTitle>
          </DialogHeader>
          
          {selectedWorker && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-slate-800 mb-2">{selectedWorker.name}</p>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div>
                  <p className="text-xs text-slate-500">Cost</p>
                  <p className="font-bold text-amber-600">{(selectedWorker.cost || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Paid</p>
                  <p className="font-bold text-emerald-600">{(selectedWorker.paid || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Remaining</p>
                  <p className={`font-bold ${((selectedWorker.cost || 0) - (selectedWorker.paid || 0)) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {((selectedWorker.cost || 0) - (selectedWorker.paid || 0)).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Payment Amount *</Label>
              <Input 
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                min="0"
                required
                placeholder="0"
              />
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input 
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPaymentModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPayment} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              💰 Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Transaction Modal */}
      <Dialog open={showAddTransactionModal} onOpenChange={setShowAddTransactionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>💳 Add Financial Transaction</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTransaction}>
            <div className="space-y-4">
              <div>
                <Label>Worker *</Label>
                <Select 
                  value={transactionForm.worker_id} 
                  onValueChange={(v) => setTransactionForm({...transactionForm, worker_id: v})}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Worker..." />
                  </SelectTrigger>
                  <SelectContent>
                    {workers.map(worker => (
                      <SelectItem key={worker.id} value={worker.id}>
                        {worker.name} - {worker.client_name || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Transaction Type *</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { value: 'ADD', label: '✅ Add', color: 'bg-emerald-100 border-emerald-300 text-emerald-700' },
                    { value: 'DEDUCT', label: '🔴 Deduct', color: 'bg-red-100 border-red-300 text-red-700' },
                    { value: 'ADJUST', label: '🔵 Adjust', color: 'bg-blue-100 border-blue-300 text-blue-700' }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      className={`p-3 rounded-lg border-2 font-semibold transition-all ${
                        transactionForm.transaction_type === type.value 
                          ? type.color + ' ring-2 ring-offset-2' 
                          : 'bg-slate-50 border-slate-200 text-slate-600'
                      }`}
                      onClick={() => setTransactionForm({...transactionForm, transaction_type: type.value})}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Amount *</Label>
                <Input 
                  type="number"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                  min="0"
                  step="0.01"
                  required
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Transaction Date *</Label>
                <Input 
                  type="datetime-local"
                  value={transactionForm.transaction_date}
                  onChange={(e) => setTransactionForm({...transactionForm, transaction_date: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={transactionForm.notes}
                  onChange={(e) => setTransactionForm({...transactionForm, notes: e.target.value})}
                  placeholder="Add any notes..."
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddTransactionModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                💾 Save Transaction
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Client Details Modal */}
      <Dialog open={showClientDetailsModal} onOpenChange={setShowClientDetailsModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedClient && (
                <span className="flex items-center gap-2">
                  {COUNTRY_FLAGS[selectedClient.country]} {selectedClient.name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <>
              {/* Client Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-slate-800 text-lg mb-2">{selectedClient.name}</p>
                    <p className="text-red-600">📍 {selectedClient.country}</p>
                    {selectedClient.contact_person && <p className="text-blue-600">👤 {selectedClient.contact_person}</p>}
                    {selectedClient.phone && <p className="text-slate-600">📞 {selectedClient.phone}</p>}
                    {selectedClient.email && <p className="text-slate-600">📧 {selectedClient.email}</p>}
                  </div>
                  <Badge className={selectedClient.currency === 'AED' ? 'bg-emerald-500' : 'bg-blue-500'}>
                    {getCurrencySymbol(selectedClient.currency)} {selectedClient.currency}
                  </Badge>
                </div>
              </div>
              
              {/* Financial Summary */}
              {(() => {
                const financials = getClientFinancials(selectedClient.id);
                const clientWorkers = getClientWorkers(selectedClient.id);
                const currencySymbol = getCurrencySymbol(selectedClient.currency);
                
                return (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6">
                      <div className="bg-blue-50 border border-blue-200 p-2 sm:p-3 rounded-lg text-center">
                        <p className="text-[10px] sm:text-xs text-blue-600 font-semibold">Active Workers</p>
                        <p className="text-lg sm:text-2xl font-bold text-blue-700">{clientWorkers.length}</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 p-2 sm:p-3 rounded-lg text-center">
                        <p className="text-[10px] sm:text-xs text-amber-600 font-semibold">Contracted</p>
                        <p className="text-sm sm:text-xl font-bold text-amber-700 truncate">{currencySymbol}{financials.totalCost.toLocaleString()}</p>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 p-2 sm:p-3 rounded-lg text-center">
                        <p className="text-[10px] sm:text-xs text-emerald-600 font-semibold">Total Paid</p>
                        <p className="text-sm sm:text-xl font-bold text-emerald-700 truncate">{currencySymbol}{financials.totalPaid.toLocaleString()}</p>
                      </div>
                      <div className={`${financials.remaining > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'} border p-2 sm:p-3 rounded-lg text-center`}>
                        <p className={`text-[10px] sm:text-xs font-semibold ${financials.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>Remaining</p>
                        <p className={`text-sm sm:text-xl font-bold truncate ${financials.remaining > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {currencySymbol}{financials.remaining.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Workers List */}
                    <h3 className="font-semibold text-slate-800 mb-3">👷 Workers</h3>
                    {clientWorkers.length === 0 ? (
                      <div className="text-center py-6 text-slate-400">
                        <span className="text-4xl block mb-2">👷</span>
                        <p>No workers assigned to this client</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {clientWorkers.map(worker => {
                          const statusInfo = WARRANTY_STATUSES[worker.warranty_status] || WARRANTY_STATUSES['not_activated'];
                          const remaining = (worker.cost || 0) - (worker.paid || 0);
                          
                          return (
                            <div key={worker.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                                    {worker.photo ? (
                                      <img src={formatBase64Image(worker.photo)} alt={worker.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-lg">👷</span>
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-800">{worker.name}</p>
                                    <p className="text-xs text-slate-500">Passport: {worker.passport_number} • {worker.nationality}</p>
                                  </div>
                                </div>
                                <WarrantyBadge status={worker.warranty_status} />
                              </div>
                              
                              <div className="grid grid-cols-4 gap-3 text-center pt-3 border-t border-slate-200">
                                <div>
                                  <p className="text-xs text-slate-500">Contract</p>
                                  <p className="font-semibold text-amber-600">{currencySymbol}{(worker.cost || 0).toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">Paid</p>
                                  <p className="font-semibold text-emerald-600">{currencySymbol}{(worker.paid || 0).toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">Remaining</p>
                                  <p className={`font-semibold ${remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                    {currencySymbol}{remaining.toLocaleString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">Warranty</p>
                                  <p className="font-semibold text-slate-800">{worker.warranty_period} months</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-2 mt-3">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="flex-1 border-blue-300 text-blue-600"
                                  onClick={() => {
                                    setSelectedWorker(worker);
                                    setShowClientDetailsModal(false);
                                    setShowAddPaymentModal(true);
                                  }}
                                >
                                  💰 Add Payment
                                </Button>
                                {!worker.warranty_activated && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="flex-1 border-emerald-300 text-emerald-600"
                                    onClick={() => {
                                      setSelectedWorker(worker);
                                      setShowClientDetailsModal(false);
                                      setShowActivateWarrantyModal(true);
                                    }}
                                  >
                                    🚀 Activate Warranty
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientDetailsModal(false)}>
              Close
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                setWorkerForm({...workerForm, client_id: selectedClient?.id || ''});
                setShowClientDetailsModal(false);
                setShowAddWorkerModal(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Worker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overseas Worker Details Modal */}
      <OverseasWorkerDetailsModal
        open={showWorkerDetailsModal}
        onClose={() => {
          setShowWorkerDetailsModal(false);
          setSelectedWorker(null);
        }}
        worker={selectedWorker}
        currencySymbol="AED"
        onPayment={(worker) => {
          setShowWorkerDetailsModal(false);
          setSelectedWorker(worker);
          setShowAddPaymentModal(true);
        }}
        onActivateWarranty={(worker) => {
          setShowWorkerDetailsModal(false);
          setSelectedWorker(worker);
          setShowActivateWarrantyModal(true);
        }}
        onDelete={(worker) => {
          setShowWorkerDetailsModal(false);
          handleDeleteWorker(worker.id);
        }}
      />
    </div>
  );
};

export default OverseasWorkersPage;
